import { Box, LinearProgress, Card, Typography } from '@material-ui/core'
import { ApexOptions } from 'apexcharts'
import { usePopup } from 'hooks/usePopup'
import { IGetAverageTimeSeparators } from 'interfaces/ISeparator'
import moment from 'moment'
import { useState, useEffect, useRef } from 'react'
import ReactApexChart from 'react-apexcharts'
import { separatorService } from 'services/separatorService'

import { useStyles } from './styles'

interface IUserData {
  username: string
  working_avg_min: number
  idle_avg_min: number
  working_total_secs: number
  idle_total_secs: number
  total_orders: number
  timesheet_min: number
  timesheet_work_perc: number
  orders_per_working_hour: number
  orders_per_timesheet_hour: number
}

interface IGlobalData {
  working_avg_min: number
  idle_avg_min: number
  working_total_secs: number
  idle_total_secs: number
  working_total_h: number
  idle_total_h: number
  efficiency_global: number
  total_orders: number
}

interface Props {
  scheduledDateGte: Date
  scheduledDateLte: Date
  selectedGroupId?: number
  refreshTrigger?: number
}

const AverageTimeOrder = (props: Props) => {
  const { scheduledDateGte, scheduledDateLte, selectedGroupId, refreshTrigger } = props
  const [userData, setUserData] = useState<IUserData[]>([])
  const [globalData, setGlobalData] = useState<IGlobalData | null>(null)
  const [noData, setNoData] = useState(false)
  const [loading, setLoading] = useState(false)
  const { addPopup } = usePopup()
  const controllerRef = useRef<AbortController | null>(null)
  const classes = useStyles()

  const getData = async () => {
    const date = (param: Date): string => {
      return moment(param).format('YYYY-MM-DD')
    }

    try {
      if (controllerRef.current) {
        controllerRef.current?.abort()
      }
      controllerRef.current = new AbortController()

      setLoading(true)
      const response: IGetAverageTimeSeparators = await separatorService.getAverageTimeSeparators(
        {
          scheduledDateGte: date(scheduledDateGte),
          scheduledDateLte: date(scheduledDateLte),
          separationGroup: selectedGroupId,
        },
        controllerRef.current,
      )

      const working_total_h = response.global.working_total_secs / 3600
      const idle_total_h = response.global.idle_total_secs / 3600
      const efficiency_global = working_total_h / (working_total_h + idle_total_h)

      const filteredUsers = response.users
        .filter(
          user =>
            user.username !== 'average_order_all_picking' &&
            (user.working_avg_min > 0 || user.idle_avg_min > 0),
        )
        .map(user => ({
          username: user.username,
          total_orders: user.total_orders,
          working_avg_min: Number(user.working_avg_min?.toFixed(1)),
          idle_avg_min: Number(user.idle_avg_min?.toFixed(1)),
          working_total_secs: user.working_total_secs,
          idle_total_secs: user.idle_total_secs,
          timesheet_min: user.timesheet_min,
          timesheet_work_perc: Number((user.timesheet_work_perc * 100)?.toFixed(1)),
          orders_per_working_hour: Number(
            (user.total_orders / (user.working_total_secs / 3600))?.toFixed(2),
          ),
          orders_per_timesheet_hour: Number(
            (user.total_orders / (user.timesheet_min / 60))?.toFixed(2),
          ),
        }))
        .sort((a, b) => b.orders_per_timesheet_hour - a.orders_per_timesheet_hour)

      const totalOrders = filteredUsers.reduce((acc, user) => acc + user.total_orders, 0)

      setGlobalData({
        working_avg_min: response.global.working_avg_min,
        idle_avg_min: response.global.idle_avg_min,
        working_total_secs: response.global.working_total_secs,
        idle_total_secs: response.global.idle_total_secs,
        working_total_h: Number(working_total_h?.toFixed(1)),
        idle_total_h: Number(idle_total_h?.toFixed(1)),
        efficiency_global: Number((efficiency_global * 100)?.toFixed(1)),
        total_orders: totalOrders,
      })

      setUserData(filteredUsers)

      if (filteredUsers.length === 0) {
        setNoData(true)
        return
      }
      setNoData(false)
    } catch (err: unknown) {
      const error = err as { message?: string; msg?: string }
      if (error?.message === 'canceled') return
      addPopup({
        title: 'Erro ao pegar os dados de tempo médio por separador',
        type: 'error',
        description: error?.message || error?.msg,
      })
    } finally {
      setLoading(false)
    }
  }

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60)
    const mins = Math.floor(minutes % 60)
    if (hours <= 0) return `${mins}m`
    return `${hours}h ${mins}m`
  }

  const avgTimeUserData = [...userData].sort((a, b) => b.working_avg_min - a.working_avg_min)

  const avgTimeOptions: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      fontFamily: 'Public Sans, sans-serif',
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: userData.length > 20 ? '70%' : userData.length > 10 ? '60%' : '50%',
        borderRadius: 4,
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: userData.length <= 15,
      formatter: (val: number) => `${val.toFixed(1)}min`,
      offsetY: -20,
      style: {
        fontSize: '11px',
        fontWeight: 600,
        colors: ['#111827'],
      },
    },
    xaxis: {
      categories: avgTimeUserData.map(user => user.username.split('.').join(' ')),
      labels: {
        rotate: avgTimeUserData.length > 8 ? -45 : avgTimeUserData.length > 5 ? -30 : 0,
        style: { fontSize: '11px' },
      },
      title: {
        text: 'Separadores',
        style: { fontSize: '14px', fontWeight: 600 },
      },
    },
    yaxis: {
      title: {
        text: 'Tempo Médio (minutos)',
        style: { fontSize: '14px', fontWeight: 600 },
      },
    },
    colors: ['#11998e'],
    grid: {
      borderColor: '#f1f1f1',
      strokeDashArray: 4,
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${val} minutos`,
      },
    },
    annotations: {
      yaxis: globalData && globalData.working_avg_min
        ? [
            {
              y: globalData.working_avg_min,
              borderColor: '#764ba2',
              strokeDashArray: 5,
              borderWidth: 2,
              label: {
                text: `Média Global: ${globalData.working_avg_min.toFixed(1)} min`,
                style: {
                  background: '#764ba2',
                  color: '#fff',
                  fontSize: '11px',
                  fontWeight: 600,
                },
                position: 'right',
              },
            },
          ]
        : [],
    },
  }

  const avgTimeSeries = [
    {
      name: 'Tempo Médio por Pedido',
      data: avgTimeUserData.map(user => parseFloat(user.working_avg_min.toFixed(1))),
    },
  ]

  const pieOptions: ApexOptions = {
    chart: {
      type: 'pie',
      toolbar: { show: false },
    },
    colors: ['#11998e', '#764ba2'],
    labels: ['Tempo Trabalhado', 'Tempo Ocioso'],
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val.toFixed(1)}%`,
      style: {
        fontSize: '14px',
        fontWeight: 600,
        colors: ['#fff'],
      },
    },
    legend: {
      position: 'bottom',
      fontSize: '14px',
      fontWeight: 500,
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${val.toFixed(1)}h`,
      },
    },
  }

  const ordersUserData = [...userData].sort((a, b) => b.total_orders - a.total_orders)

  const verticalBarOptions: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
    },
    colors: ['#764ba2'],
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: userData.length > 20 ? '70%' : userData.length > 10 ? '60%' : '50%',
        borderRadius: 4,
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: ordersUserData.length <= 15,
      formatter: (val: number) => val.toString(),
      offsetY: -20,
      style: {
        fontSize: '11px',
        colors: ['#111827'],
        fontWeight: 600,
      },
    },
    xaxis: {
      categories: ordersUserData.map(user => user.username.split('.').join(' ')),
      labels: {
        rotate: ordersUserData.length > 8 ? -45 : ordersUserData.length > 5 ? -30 : 0,
        style: { fontSize: '11px' },
      },
      title: {
        text: 'Separadores',
        style: { fontSize: '14px', fontWeight: 600 },
      },
    },
    yaxis: {
      title: {
        text: 'Número de Pedidos',
        style: { fontSize: '14px', fontWeight: 600 },
      },
      labels: {
        formatter: (val: number) => val.toString(),
      },
    },
    grid: {
      strokeDashArray: 3,
      borderColor: '#E5E7EB',
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${val} pedidos`,
      },
    },
    annotations: {
      yaxis: globalData && globalData.total_orders && userData.length > 0
        ? [
            {
              y: globalData.total_orders / userData.length,
              borderColor: '#11998e',
              strokeDashArray: 5,
              borderWidth: 2,
              label: {
                text: `Média: ${(globalData.total_orders / userData.length).toFixed(1)} pedidos`,
                style: {
                  background: '#11998e',
                  color: '#fff',
                  fontSize: '11px',
                  fontWeight: 600,
                },
                position: 'right',
              },
            },
          ]
        : [],
    },
  }

  const timeWorkedUserData = [...userData].sort((a, b) => b.timesheet_min - a.timesheet_min)

  const averageTimesheet = userData.length > 0 
    ? userData.reduce((acc, user) => acc + user.timesheet_min, 0) / userData.length 
    : 0

  const timeWorkedBarOptions: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      fontFamily: 'Public Sans, sans-serif',
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: userData.length > 20 ? '70%' : userData.length > 10 ? '60%' : '50%',
        borderRadius: 4,
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: timeWorkedUserData.length <= 15,
      formatter: (val: number) => formatTime(val),
      offsetY: -20,
      style: {
        fontSize: '11px',
        fontWeight: 600,
        colors: ['#111827'],
      },
    },
    xaxis: {
      categories: timeWorkedUserData.map(user => user.username.split('.').join(' ')),
      labels: {
        rotate: timeWorkedUserData.length > 8 ? -45 : timeWorkedUserData.length > 5 ? -30 : 0,
        style: { fontSize: '11px' },
      },
      title: {
        text: 'Separadores',
        style: { fontSize: '14px', fontWeight: 600 },
      },
    },
    yaxis: {
      title: {
        text: 'Tempo Trabalhado',
        style: { fontSize: '14px', fontWeight: 600 },
      },
      labels: {
        formatter: (val: number) => formatTime(val),
      },
    },
    colors: ['#11998e'],
    grid: {
      borderColor: '#f1f1f1',
      strokeDashArray: 4,
    },
    tooltip: {
      y: {
        formatter: (val: number) => formatTime(val),
      },
      custom: ({ dataPointIndex }) => {
        const user = timeWorkedUserData[dataPointIndex]
        return `
          <div style="padding: 12px; background: white; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
            <div style="font-weight: 600; margin-bottom: 8px; color: #111827; text-transform: capitalize;">
              ${user.username.split('.').join(' ')}
            </div>
            <div style="margin-bottom: 4px; color: #6b7280;">
              📦 Pedidos: <strong>${user.total_orders}</strong>
            </div>
            <div style="margin-bottom: 4px; color: #1976d2;">
              ⏱️ Tempo trabalhado: <strong>${formatTime(user.timesheet_min)}</strong>
            </div>
            <div style="margin-bottom: 4px; color: #1976d2;">
              📊 Aproveitamento: <strong>${user.timesheet_work_perc.toFixed(1)}%</strong>
            </div>
            <div style="color: #6b7280; font-size: 12px;">
              🚀 Produtividade: ${user.orders_per_timesheet_hour} ped/h
            </div>
          </div>
        `
      },
    },
    annotations: {
      yaxis: averageTimesheet > 0
        ? [
            {
              y: averageTimesheet,
              borderColor: '#764ba2',
              strokeDashArray: 5,
              borderWidth: 2,
              label: {
                text: `Média: ${formatTime(averageTimesheet)}`,
                style: {
                  background: '#764ba2',
                  color: '#fff',
                  fontSize: '11px',
                  fontWeight: 600,
                },
                position: 'right',
              },
            },
          ]
        : [],
    },
  }

  // Dados ordenados por eficiência para o gráfico de barras
  const efficiencyUserData = [...userData].sort(
    (a, b) => b.timesheet_work_perc - a.timesheet_work_perc,
  )

  const efficiencyBarOptions: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
    },
    colors: ['#4facfe'],
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth:
          efficiencyUserData.length > 20 ? '70%' : efficiencyUserData.length > 10 ? '60%' : '50%',
        borderRadius: 2,
      },
    },
    dataLabels: {
      enabled: efficiencyUserData.length <= 10,
      formatter: (val: number) => `${val.toFixed(0)}%`,
      style: {
        fontSize: '10px',
        colors: ['#111827'],
        fontWeight: 600,
      },
    },
    xaxis: {
      categories: efficiencyUserData.map(user => user.username.split('.').join(' ')),
      labels: {
        rotate: efficiencyUserData.length > 8 ? -45 : 0,
        style: { fontSize: '11px' },
      },
      title: {
        text: 'Separadores',
        style: { fontSize: '14px', fontWeight: 600 },
      },
    },
    yaxis: {
      title: {
        text: '% Eficiência (Timesheet)',
        style: { fontSize: '14px', fontWeight: 600 },
      },
      labels: {
        formatter: (val: number) => `${val.toFixed(0)}%`,
      },
      max: 100,
    },
    grid: {
      strokeDashArray: 3,
      borderColor: '#E5E7EB',
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${val.toFixed(1)}% eficiente`,
      },
    },
    annotations: {
      yaxis: globalData
        ? [
            {
              y: globalData.efficiency_global,
              borderColor: '#764ba2',
              strokeDashArray: 5,
              borderWidth: 2,
              label: {
                text: `Média: ${globalData.efficiency_global}%`,
                style: {
                  background: '#764ba2',
                  color: '#fff',
                  fontSize: '11px',
                },
                position: 'right',
              },
            },
          ]
        : [],
    },
  }

  const paretoUserData = [...userData].sort((a, b) => b.idle_total_secs - a.idle_total_secs)

  const averageIdleSecs = userData.length > 0 && globalData
    ? globalData.idle_total_secs / userData.length
    : 0

  const paretoOptions: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
    },
    colors: ['#ff6b6b'],
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth:
          paretoUserData.length > 20 ? '70%' : paretoUserData.length > 10 ? '60%' : '50%',
        borderRadius: 2,
      },
    },
    dataLabels: {
      enabled: paretoUserData.length <= 8,
      formatter: (val: number) => formatTime(val / 60),
      style: {
        fontSize: '10px',
        colors: ['#111827'],
        fontWeight: 500,
      },
    },
    xaxis: {
      categories: paretoUserData.map(user => user.username.split('.').join(' ')),
      labels: {
        rotate: paretoUserData.length > 8 ? -45 : 0,
        style: { fontSize: '11px' },
      },
      title: {
        text: 'Separadores',
        style: { fontSize: '14px', fontWeight: 600 },
      },
    },
    yaxis: {
      title: {
        text: 'Tempo Ocioso Acumulado (min)',
        style: { fontSize: '14px', fontWeight: 600 },
      },
      labels: {
        formatter: (val: number) => formatTime(val / 60),
      },
    },
    grid: {
      strokeDashArray: 3,
      borderColor: '#E5E7EB',
    },
    tooltip: {
      y: {
        formatter: (val: number) => formatTime(val / 60),
      },
    },
    annotations: {
      yaxis: averageIdleSecs > 0
        ? [
            {
              y: averageIdleSecs,
              borderColor: '#11998e',
              strokeDashArray: 5,
              borderWidth: 2,
              label: {
                text: `Média: ${formatTime(averageIdleSecs / 60)}`,
                style: {
                  background: '#11998e',
                  color: '#fff',
                  fontSize: '11px',
                  fontWeight: 600,
                },
                position: 'right',
              },
            },
          ]
        : [],
    },
  }

  const pieSeries = globalData ? [globalData.working_total_h, globalData.idle_total_h] : []

  const verticalBarSeries = [
    {
      name: 'Pedidos Processados',
      data: ordersUserData.map(user => user.total_orders),
    },
  ]

  const timeWorkedBarSeries = [
    {
      name: 'Tempo Trabalhado',
      data: timeWorkedUserData.map(user => user.timesheet_min),
    },
  ]

  const efficiencyBarSeries = [
    {
      name: '% Eficiência',
      data: efficiencyUserData.map(user => user.timesheet_work_perc),
    },
  ]

  const paretoSeries = [
    {
      name: 'Tempo Ocioso Acumulado',
      data: paretoUserData.map(user => user.idle_total_secs),
    },
  ]

  useEffect(() => {
    getData()
  }, [scheduledDateGte, scheduledDateLte, selectedGroupId, refreshTrigger])

  return (
    <Box>
      <LinearProgress
        variant="indeterminate"
        style={{ width: '100%', marginBottom: '0.5rem', opacity: loading ? 1 : 0 }}
      />

      {globalData && (
        <Box style={{ marginBottom: '1.5rem' }}>
          <Box
            display="grid"
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1rem',
              marginBottom: '1.5rem',
            }}
          >
            <Card
              elevation={0}
              style={{
                padding: '24px',
                border: '1px solid #f0f0f0',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography
                    variant="h4"
                    style={{
                      color: '#fff',
                      fontWeight: 800,
                      marginBottom: '8px',
                    }}
                  >
                    {globalData.total_orders.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.8)' }}>
                    Total de Pedidos
                  </Typography>
                </Box>
                <Box
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: '12px',
                    padding: '12px',
                    fontSize: '24px',
                  }}
                >
                  📦
                </Box>
              </Box>
            </Card>

            <Card
              elevation={0}
              style={{
                padding: '24px',
                border: '1px solid #f0f0f0',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography
                    variant="h4"
                    style={{
                      color: '#fff',
                      fontWeight: 800,
                      marginBottom: '8px',
                    }}
                  >
                    {globalData.working_avg_min} min
                  </Typography>
                  <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.8)' }}>
                    Tempo Médio por Pedido
                  </Typography>
                </Box>
                <Box
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: '12px',
                    padding: '12px',
                    fontSize: '24px',
                  }}
                >
                  ⚡
                </Box>
              </Box>
            </Card>

            <Card
              elevation={0}
              style={{
                padding: '24px',
                border: '1px solid #f0f0f0',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ffa726 100%)',
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography
                    variant="h4"
                    style={{
                      color: '#fff',
                      fontWeight: 800,
                      marginBottom: '8px',
                    }}
                  >
                    {formatTime(globalData.idle_avg_min)}
                  </Typography>
                  <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.8)' }}>
                    Tempo Ocioso Médio
                  </Typography>
                </Box>
                <Box
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: '12px',
                    padding: '12px',
                    fontSize: '24px',
                  }}
                >
                  ⏰
                </Box>
              </Box>
            </Card>

            <Card
              elevation={0}
              style={{
                padding: '24px',
                border: '1px solid #f0f0f0',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography
                    variant="h4"
                    style={{
                      color: '#fff',
                      fontWeight: 800,
                      marginBottom: '8px',
                    }}
                  >
                    {globalData.efficiency_global || 0}%
                  </Typography>
                  <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.8)' }}>
                    Aproveitamento
                  </Typography>
                </Box>
                <Box
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: '12px',
                    padding: '12px',
                    fontSize: '24px',
                  }}
                >
                  📊
                </Box>
              </Box>
            </Card>
          </Box>
        </Box>
      )}

      {noData && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          style={{
            height: '300px',
            backgroundColor: '#F9FAFB',
            borderRadius: '12px',
          }}
        >
          <Typography variant="h6" style={{ color: '#637381' }}>
            Sem dados para o período selecionado
          </Typography>
        </Box>
      )}

      {!noData && userData.length > 0 && (
        <Box style={{ marginBottom: '1.5rem' }}>
          <Card
            elevation={0}
            style={{
              padding: '1rem',
              border: '1px solid #f0f0f0',
              borderRadius: '16px',
              background: '#fff',
            }}
          >
            <Typography
              variant="h6"
              style={{
                marginBottom: '1rem',
                fontWeight: 500,
                color: '#212B36',
                paddingLeft: '1.5rem',
              }}
            >
              Tempo Médio (min)
            </Typography>
            <ReactApexChart
              options={avgTimeOptions}
              series={avgTimeSeries}
              type="bar"
              height={400}
            />
          </Card>
        </Box>
      )}

      {!noData && userData.length > 0 && (
        <Box style={{ marginBottom: '1rem' }}>
          <Card
            elevation={0}
            style={{
              padding: '1rem',
              marginBottom: '1rem',
              border: '1px solid #f0f0f0',
              borderRadius: '16px',
              background: '#fff',
            }}
          >
            <Typography
              variant="h6"
              style={{
                marginBottom: '1rem',
                fontWeight: 500,
                color: '#212B36',
                paddingLeft: '1.5rem',
              }}
            >
              Quantidade de pedido por separador
            </Typography>
            <ReactApexChart
              options={verticalBarOptions}
              series={verticalBarSeries}
              type="bar"
              height={400}
            />
          </Card>

          <Box className={classes.chartContainer}>
            <Card
              elevation={0}
              style={{
                border: '1px solid #f0f0f0',
                borderRadius: '16px',
                background: '#fff',
                padding: '1rem',
                flex: 1,
                minWidth: '300px',
              }}
            >
              <Typography
                variant="h6"
                style={{
                  fontWeight: 500,
                  color: '#212B36',
                  paddingLeft: '1.5rem',
                  marginBottom: '1rem',
                  flex: 1,
                }}
              >
                Aproveitamento
              </Typography>
              <Box>
                <ReactApexChart options={pieOptions} series={pieSeries} type="pie" height={350} />
              </Box>
            </Card>

            <Card
              elevation={0}
              style={{
                padding: '1rem',
                border: '1px solid #f0f0f0',
                borderRadius: '16px',
                background: '#fff',
                flex: 3,
                minWidth: '300px',
              }}
            >
              <Typography
                variant="h6"
                style={{
                  marginBottom: '1rem',
                  fontWeight: 500,
                  color: '#212B36',
                  paddingLeft: '1.5rem',
                }}
              >
                % Aproveitamento por Separador
              </Typography>
              <ReactApexChart
                options={efficiencyBarOptions}
                series={efficiencyBarSeries}
                type="bar"
                height={400}
              />
            </Card>
          </Box>

          <Box style={{ marginTop: '1rem' }}>
            <Card
              elevation={0}
              style={{
                padding: '1rem',
                border: '1px solid #f0f0f0',
                borderRadius: '16px',
                background: '#fff',
              }}
            >
              <Typography
                variant="h6"
                style={{
                  marginBottom: '1rem',
                  fontWeight: 500,
                  color: '#212B36',
                  paddingLeft: '1.5rem',
                }}
              >
                Tempo Trabalhado por Colaborador
              </Typography>
              <ReactApexChart
                options={timeWorkedBarOptions}
                series={timeWorkedBarSeries}
                type="bar"
                height={400}
              />
            </Card>
          </Box>
        </Box>
      )}
      {!noData && userData.length > 0 && globalData && (
        <Box style={{ marginBottom: '2rem' }}>
          <Box>
            {/* Ranking: Maiores Tempos Ociosos */}
            <Card
              elevation={0}
              style={{
                padding: '1rem',
                border: '1px solid #f0f0f0',
                borderRadius: '16px',
                background: '#fff',
              }}
            >
              <Typography
                variant="h6"
                style={{
                  marginBottom: '1rem',
                  fontWeight: 500,
                  color: '#212B36',
                  paddingLeft: '1.5rem',
                }}
              >
                Ranking: Maiores Tempos Ociosos (min)
              </Typography>
              <ReactApexChart
                options={paretoOptions}
                series={paretoSeries}
                type="bar"
                height={350}
              />
            </Card>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default AverageTimeOrder
